import SearchableSelect from '../Form/SearchableSelect';
import {
  filterByProgramType,
  programTypesFromCategories,
} from '../../domain/adminUser/enrollSequence';
import {
  applyEnrollFieldChange,
  listMatchingBatches,
  matchingBatchId,
} from '../../domain/adminUser/enrollFieldChange';
import { canCreateEnrollmentBatch } from '../../domain/adminUser/enrollmentRequirement';
import BatchNamingHint from './BatchNamingHint';
import useTrainingCenters, { centerOptionsWithFallback } from '../../hooks/useTrainingCenters';

const MODES = [
  { value: 'Online', label: 'Online' },
  { value: 'Offline', label: 'Offline' },
];

export default function EditableEnrollFields({
  formData,
  dispatch,
  courses = [],
  courseCategories = [],
  batches = [],
  getFieldStyle,
  batchOptional = false,
  onCreateBatch,
  creatingBatch = false,
  createBatchMessage = '',
}) {
  const { options: catalogCenters } = useTrainingCenters();
  const centerOptions = centerOptionsWithFallback(
    catalogCenters,
    batches,
    [formData.locationOfCenter]
  );
  const programTypes = programTypesFromCategories(courseCategories);
  const filteredCourses = formData.programType
    ? filterByProgramType(courses, formData.programType)
    : courses;
  const filteredCategories = formData.programType
    ? filterByProgramType(courseCategories, formData.programType)
    : courseCategories;
  const matches = listMatchingBatches(batches, formData);
  const matchedId = matchingBatchId(batches, formData, formData.batchId);
  const ambiguousMatch = matches.length > 1 && !matchedId;
  const willCreate = Boolean(
    formData.courseId &&
      formData.modeOfCourse &&
      formData.batchStartDate &&
      !matchedId &&
      !ambiguousMatch
  );
  const canCreateNow = canCreateEnrollmentBatch(formData);

  const apply = (field, value) => {
    const next = applyEnrollFieldChange({
      field,
      value,
      formData,
      courses,
      batches,
    });
    dispatch({ type: 'UPDATE_FIELDS', fields: next });
  };

  const onSelect = (name) => (event) => apply(name, event.target.value);
  const s = (value) =>
    typeof getFieldStyle === 'function'
      ? getFieldStyle(value)
      : {
          backgroundColor: 'var(--color-surface)',
          border: '2px solid var(--color-border)',
          color: 'var(--color-text)',
        };

  const opt = batchOptional ? (
    <span className="text-xs font-normal" style={{ color: 'var(--color-text-secondary)' }}>
      {' '}
      (optional)
    </span>
  ) : null;

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
          Program type{opt}
        </label>
        <SearchableSelect
          name="programType"
          value={formData.programType || ''}
          onChange={onSelect('programType')}
          className="w-full"
          placeholder="Select program type"
          options={programTypes}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
          Course Name{opt}
        </label>
        <SearchableSelect
          name="courseId"
          value={formData.courseId || ''}
          onChange={onSelect('courseId')}
          className="w-full"
          placeholder="Select course"
          options={filteredCourses}
          getOptionValue={(course) => (typeof course === 'object' ? course.id : course)}
          getOptionLabel={(course) =>
            typeof course === 'object' ? course.courseName || course.name : course
          }
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
          Course Category{opt}
        </label>
        <SearchableSelect
          name="courseCategoryId"
          value={formData.courseCategoryId || ''}
          onChange={onSelect('courseCategoryId')}
          className="w-full"
          placeholder="Select category"
          options={filteredCategories}
          getOptionValue={(cat) => (typeof cat === 'object' ? cat.id : cat)}
          getOptionLabel={(cat) => {
            if (typeof cat !== 'object') return cat;
            const name = cat.categoryName || cat.name || '';
            const code = cat.categoryCode || cat.code || '';
            return code ? `${name} - ${code}` : name;
          }}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
          Mode{opt}
        </label>
        <SearchableSelect
          name="modeOfCourse"
          value={formData.modeOfCourse || ''}
          onChange={onSelect('modeOfCourse')}
          className="w-full"
          placeholder="Select mode"
          options={MODES}
        />
      </div>
      {String(formData.modeOfCourse || '').trim() === 'Offline' ? (
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
            Center{opt}
          </label>
          <SearchableSelect
            name="locationOfCenter"
            value={formData.locationOfCenter || ''}
            onChange={onSelect('locationOfCenter')}
            className="w-full"
            placeholder="Select Location of Center"
            creatable
            options={centerOptions}
          />
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Shared list. Add or rename centers under Training centers. A new typed value is saved to that list.
          </p>
        </div>
      ) : null}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
          Batch Start Date{opt}
        </label>
        <input
          type="date"
          name="batchStartDate"
          value={formData.batchStartDate || ''}
          onChange={(e) => apply('batchStartDate', e.target.value)}
          className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
          style={s(formData.batchStartDate)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
          Course Completion Date
        </label>
        <input
          type="date"
          name="courseCompletionDate"
          value={formData.courseCompletionDate || ''}
          onChange={(e) => apply('courseCompletionDate', e.target.value)}
          className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
          style={s(formData.courseCompletionDate)}
        />
      </div>
      <BatchNamingHint
        batches={batches}
        courseId={formData.courseId}
        willCreate={willCreate}
        existingName={
          matchedId
            ? (batches.find((batch) => String(batch?.id) === String(matchedId))?.batchName || '')
            : ''
        }
      />
      {typeof onCreateBatch === 'function' ? (
        <div className="space-y-2">
          <button
            type="button"
            onClick={onCreateBatch}
            disabled={!canCreateNow || creatingBatch || ambiguousMatch}
            className="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '2px solid var(--color-border)',
              color: 'var(--color-text)',
            }}
          >
            {creatingBatch ? 'Creating batch…' : 'Create batch from these fields'}
          </button>
          {createBatchMessage ? (
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {createBatchMessage}
            </p>
          ) : ambiguousMatch ? (
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              More than one batch has this course, mode, and start date. Select the batch above.
            </p>
          ) : willCreate ? (
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              No matching batch yet. Use the button above, or a new batch is created when you
              save with these fields filled.
            </p>
          ) : matchedId ? (
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              Matches an existing batch — select Create batch to attach it, or it resolves on
              save.
            </p>
          ) : null}
        </div>
      ) : willCreate ? (
        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          No batch exists for this combination. A new batch will be created from these
          course, mode, center, and dates when you save.
        </p>
      ) : null}
    </div>
  );
}
